import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import ReactTableWrapper from './ReactTableWrapper';
import SectionPagination from './SectionPagination';
import useManualReactTableState from './ManualReactTableState';
import {
    convertDataToListingDisplay,
    convertManifestListingToAReadableFormat,
    getGraphQLTotalRecords,
} from '../selectors/converterSelector';
import {
    axiosRequestManifestList,
    axiosPackOrder,
    axiosAwaitingPickupOrder,
    axiosUnpackOrder,
    axiosPrintManifest, axiosUnprintManifest
} from '../actions/dispatchActions';
import LocalizedMessage from 'react-l10n';
import { breakpoints, getBreakpoint } from '../dom/_site';
import { handleAxiosErrors } from '../_utils';
import { getTranslation } from '../lang/main';
import FieldSelect from './fields/FieldSelect';
import InlineFieldGroup from './fields/InlineFieldGroup';

const manifestStatusDropdown = [
    {
        label: getTranslation('UNPRINTED'),
        value: 'Unprinted',
    },
    {
        label: getTranslation('PRINTED'),
        value: 'Printed',
    },
];

/**
 * @param props
 * @returns {*}
 * @constructor
 */
function ManifestList(props) {
    let mainTableWrapper;

    const {
        className,
        requestPropWatcher = '',
        sortBy = 'Created',
        sortDirection = 'DESC',
    } = props;

    // State management
    // --------------------
    const mrtState = useManualReactTableState({
        listParams: {
            sortBy,
            sortDirection,
        },
    });

    // Core
    const { isLoading, toggleLoading } = mrtState;
    const {
        list,
        listParams,
        getDisplayedList,
        setListDisplay,
        updateList,
    } = mrtState;
    const { getFilters, setFilters } = mrtState;
    const {
        getTotalRecords,
        setTotalRecords,
        getActivePage,
        setActivePage,
        getTotalPages,
        getPaginationPages,
    } = mrtState;
    const { handleOnTableUpdate } = mrtState;
    const listEffectWatcher = mrtState.getEffectWatcher();

    // User Input
    const { isUserInputLoading, toggleUserInputLoading } = mrtState;
    const { userInput, clearUserInput, updateUserInput } = mrtState;

    // Effects
    // --------------------
    // - Main Request
    useEffect(() => {
        request();
    }, [listEffectWatcher, requestPropWatcher]);

    // Functions
    // --------------------
    /**
     * Request List
     */
    const request = () => {
        toggleLoading('list', true);

        return axiosRequestManifestList(listParams)
            .then((response) => {
                const list = convertManifestListingToAReadableFormat(
                    response.data.data.readManifests
                );
                const listingDisplay = convertDataToListingDisplay(
                    response.data.data.readManifests,
                    'ID'
                );
                const total = getGraphQLTotalRecords(
                    response.data.data.readManifests
                );

                console.log('list', list);

                // wait for async requests
                Promise.all([
                    setListDisplay(listingDisplay),
                    updateList(list),
                    setTotalRecords(total),
                ]).then(() => {
                    toggleLoading('list', false);
                });
            })
            .catch((error) => {
                handleAxiosErrors(error);
                toggleLoading('list', false);
            });
    };

    /**
     * Save manifest status
     *
     * @param identifier
     * @param key
     * @param value
     * @returns {Q.Promise<T> | Promise<any> | Promise<T> | *}
     */
    const saveManifestStatus = (identifier, key, status) => {
        let axiosCall;
        let graphqlName = '';

        switch(status) {
            case 'Printed':
                axiosCall = axiosPrintManifest;
                graphqlName = 'printManifest';
                break;
            case 'Unprinted':
                axiosCall = axiosUnprintManifest;
                graphqlName = 'unprintManifest';
                break;
        }

        if(!axiosCall) return;

        toggleUserInputLoading(identifier, key, true);

        return axiosCall(identifier).then((response) => {
            const { data } = response;

            toggleUserInputLoading(identifier, key, false);

            console.log(data);

            if (typeof data.data[graphqlName] != 'undefined') {
                let updatedList = {
                    [identifier]: {
                        Printed: data.data[graphqlName].Printed
                    },
                };

                console.log('Running updateList', updatedList);

                Promise.all([updateList(updatedList)]).then(() => {
                    clearUserInput(identifier, key);
                });
            } else {
                clearUserInput(identifier, key);
            }
        })
        .catch((error) => {
            handleAxiosErrors(error);
        })
        .finally(() => {
            toggleUserInputLoading(identifier, key, false);
        });
    };

    /**
     * Get table props (for React Table)
     *
     * @returns {{list: {SubComponent: (function(*): *), columns: *[], showPagination: boolean, manual: boolean, getTrGroupProps: (function(*, *, *, *): {className: string})}}}
     */
    const getTableProps = () => {
        let tableProps = {
            list: {
                // manual: true,
                // noDataText: <span>No items found. <br/>See results from our full range below.</span>,
                columns: [
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'MANIFEST_ID'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.value}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        minWidth: undefined,
                        accessor: 'InternalItemID',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'CARRIER'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.value}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        headerClassName: 'hidden-xs',
                        className: 'hidden-xs',
                        minWidth: undefined,
                        accessor: 'ShipperAccountTitle',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'NO_OF_ARTICLES'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.value}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        headerClassName: 'hidden-xs',
                        className: 'hidden-xs',
                        minWidth: undefined,
                        accessor: 'ParcelCount',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'PROCESSED'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.original.CreatedNice}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        minWidth: undefined,
                        accessor: 'Created',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'MANIFEST'} />
                                    </span>
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            if(!row.value) {
                                return (
                                    <React.Fragment>
                                        <span className='react-table__info'>
                                            N/A
                                        </span>
                                    </React.Fragment>
                                );
                            }

                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        <a href={row.value} target="_blank">Download</a>
                                    </span>
                                </React.Fragment>
                            );
                        },
                        sortable: false,
                        minWidth: undefined,
                        accessor: 'FileUrl',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'MANIFEST_STATUS'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            const identifier = row.original.ID;
                            const inputKey = 'Printed';
                            let hasInlineEdit = false;
                            let currentValue = row.original[inputKey] === true ? 'Printed' : 'Unprinted';

                            if (typeof row.value == 'undefined' || row.value === null) {
                                return '';
                            }

                            if (typeof userInput[identifier] != 'undefined' && typeof userInput[identifier][inputKey] != 'undefined') {
                                hasInlineEdit = currentValue != userInput[identifier][inputKey];
                                currentValue = userInput[identifier][inputKey];
                            }

                            return (
                                <React.Fragment>
                                    <span className='react-table__info react-table__info--default'>
                                        <InlineFieldGroup
                                            hasUndo={true}
                                            isModified={hasInlineEdit}
                                            saveButtonLoading={isUserInputLoading(identifier, inputKey)}
                                            onSaveClick={() => saveManifestStatus(identifier, inputKey, currentValue)}
                                            onUndoClick={() => clearUserInput(identifier, inputKey)}
                                        >
                                            <FieldSelect
                                                label={getTranslation('STATUS')}
                                                animatedLabel={getTranslation('STATUS')}
                                                value={currentValue}
                                                name={inputKey}
                                                options={manifestStatusDropdown}
                                                onChange={(e) => updateUserInput(identifier, e.target.name, e.target.value)}
                                            />
                                        </InlineFieldGroup>
                                    </span>
                                </React.Fragment>
                            );
                        },
                        minWidth: undefined,
                        accessor: 'Printed',
                    },
                    {
                        expander: true,
                        Expander: ({ isExpanded, ...rest }) => {
                            return (
                                <React.Fragment>
                                    <button
                                        type='button'
                                        className='react-table__extra-data-toggle'
                                    >
                                        <span className='icon icon-chevron-thick' />
                                    </button>
                                </React.Fragment>
                            );
                        },
                        headerClassName: 'visible-xs',
                        className: 'visible-xs',
                        width: 45,
                        sortable: false,
                        accessor: 'ID',
                    },
                ],
                showPagination: false,
                getTrGroupProps: (state, row, col, instance) => {
                    const { viewIndex } = row;

                    const isExpanded =
                        state.expanded &&
                        state.expanded.hasOwnProperty(viewIndex) &&
                        state.expanded[viewIndex];

                    let classes = 'react-table__row react-table__row--selectable';
                    classes += ` ${isExpanded ? 'react-table__row--expanded' : ''}`;

                    return {
                        className: classes,
                    };
                },
                SubComponent: (row) => {
                    const {
                        ShipperAccountTitle,
                        ParcelCount,
                    } = row.original;

                    return (
                        <div className='react-table__extra-data'>
                            <div className='row'>

                                <div className='col-xs-6 vspace vspace--small'>
                                    <div className='react-table__heading'>
                                        <LocalizedMessage id={'CARRIER'} />
                                    </div>
                                    <div className='react-table__info'>
                                        {ShipperAccountTitle}
                                    </div>
                                </div>
                                <div className='col-xs-6 vspace vspace--small'>
                                    <div className='react-table__heading'>
                                        <LocalizedMessage id={'NO_OF_ARTICLES'} />
                                    </div>
                                    <div className='react-table__info'>
                                        {ParcelCount}
                                    </div>
                                </div>

                            </div>
                        </div>
                    );
                },
            },
        };

        return tableProps;
    };

    return (
        <React.Fragment>
            <div className={classnames('card card--border', className)}>
                <div className='card__header'>
                    <div className='row row--vcenter-md'>
                        <div className='col-xs-12 col-md-6 vspace vspace--small'>
                            <h2 className='heading heading--lv4'>
                                <LocalizedMessage id={'MANIFEST_LIST'} />
                            </h2>
                        </div>
                    </div>
                </div>
                <ReactTableWrapper
                    key={getBreakpoint()}
                    loading={isLoading.list}
                    onFetchData={handleOnTableUpdate}
                    data={getDisplayedList()}
                    reactTable={getTableProps()}
                    ref={(r) => (mainTableWrapper = r)}
                />
            </div>
            <SectionPagination
                activePage={getActivePage()}
                totalPages={getTotalPages()}
                pages={getPaginationPages()}
                onPaginationClick={(pageNum) => setActivePage(pageNum)}
            />
        </React.Fragment>
    );
}

export default ManifestList;
